import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { num } from '../lib/format';
import { Button, Card, CardHead, Field, Notice, Select, useToast } from '../components/ui';

type Position = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

interface Settings {
  enabled: boolean;
  centerScale: number;
  centerOpacity: number;
  cornerScale: number;
  cornerOpacity: number;
  cornerPosition: Position;
}

interface WatermarkState extends Settings {
  logoKey: string;
  logoUrl: string;
  status: { total: number; done: number; pending: number };
}

const pick = (s: WatermarkState): Settings => ({
  enabled: s.enabled,
  centerScale: s.centerScale,
  centerOpacity: s.centerOpacity,
  cornerScale: s.cornerScale,
  cornerOpacity: s.cornerOpacity,
  cornerPosition: s.cornerPosition,
});

function Slider({ label, value, min, max, step, onChange, show }: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  show: (v: number) => string;
}) {
  return (
    <Field label={`${label}: ${show(value)}`}>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: '100%' }} />
    </Field>
  );
}

/**
 * The ISawBazaar watermark on every listing photo: which logo, how big, how see-
 * through, and which corner. Saving redoes every listing photo from its
 * original in the background.
 */
export function WatermarkPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const state = useQuery({
    queryKey: ['watermark'],
    queryFn: () => api.get<WatermarkState>('/admin/watermark'),
    refetchInterval: (q) => ((q.state.data?.status.pending ?? 0) > 0 ? 5000 : false),
  });
  const [draft, setDraft] = useState<Settings | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (state.data && !draft) setDraft(pick(state.data));
  }, [state.data, draft]);

  // A fresh preview a moment after the sliders stop moving.
  useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => {
      api
        .post<{ image: string }>('/admin/watermark/preview', draft)
        .then((r) => setPreview(r.image))
        .catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
  }, [draft, state.data?.logoKey]);

  const done = (msg: string) => {
    toast(msg, 'success');
    qc.invalidateQueries({ queryKey: ['watermark'] });
  };
  const save = useMutation({
    mutationFn: () => api.put<WatermarkState>('/admin/watermark', draft),
    onSuccess: () => done('Saved. Every listing photo is being redone with these settings.'),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const reapply = useMutation({
    mutationFn: () => api.post('/admin/watermark/reapply'),
    onSuccess: () => done('Every listing photo is being redone.'),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const uploadLogo = useMutation({
    mutationFn: (imageBase64: string) => api.post('/admin/watermark/logo', { imageBase64 }),
    onSuccess: () => done('New logo saved. Every listing photo is being redone with it.'),
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const resetLogo = useMutation({
    mutationFn: () => api.post('/admin/watermark/logo/reset'),
    onSuccess: () => done('Back to the standard ISawBazaar logo.'),
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast('The logo can be up to 1 MB', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => uploadLogo.mutate(String(reader.result));
    reader.readAsDataURL(file);
  };

  const s = state.data;
  if (!s || !draft) return <div className="skeleton" style={{ height: 320 }} />;
  const set = (patch: Partial<Settings>) => setDraft({ ...draft, ...patch });
  const pct = (v: number) => `${Math.round(v * 100)}%`;

  return (
    <div className="stack">
      <Notice tone="info" title="Every listing photo carries the ISawBazaar watermark">
        It is burnt into the photo on the server when it is uploaded, so the app, the website, the full-screen viewer,
        downloads and shares all show it. The original is kept privately and is never shown. {num(s.status.done)} of{' '}
        {num(s.status.total)} listing photos are done{s.status.pending ? `; ${num(s.status.pending)} in progress` : ''}.
      </Notice>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1.4fr)', gap: 16, alignItems: 'start' }}>
        <Card>
          <CardHead title="Settings" />
          <div className="stack" style={{ paddingTop: 'var(--s3)' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={draft.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
              Watermark listing photos
            </label>
            <Slider label="Centre logo size" value={draft.centerScale} min={0.1} max={0.9} step={0.05} onChange={(v) => set({ centerScale: v })} show={pct} />
            <Slider label="Centre transparency (opacity)" value={draft.centerOpacity} min={0} max={1} step={0.05} onChange={(v) => set({ centerOpacity: v })} show={pct} />
            <Slider label="Corner logo size" value={draft.cornerScale} min={0.05} max={0.5} step={0.01} onChange={(v) => set({ cornerScale: v })} show={pct} />
            <Slider label="Corner opacity" value={draft.cornerOpacity} min={0} max={1} step={0.05} onChange={(v) => set({ cornerOpacity: v })} show={pct} />
            <Field label="Corner position">
              <Select
                value={draft.cornerPosition}
                onChange={(e) => set({ cornerPosition: e.target.value as Position })}
                options={[
                  { value: 'bottom-right', label: 'Bottom right' },
                  { value: 'bottom-left', label: 'Bottom left' },
                  { value: 'top-right', label: 'Top right' },
                  { value: 'top-left', label: 'Top left' },
                ]}
              />
            </Field>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save and apply</Button>
              <Button loading={reapply.isPending} onClick={() => reapply.mutate()}>Redo all photos</Button>
            </div>
          </div>
        </Card>

        <Card>
          <CardHead title="Preview" sub="A sample listing photo with these settings" />
          {preview ? (
            <img src={preview} alt="Watermark preview" style={{ width: '100%', borderRadius: 10, marginTop: 'var(--s3)' }} />
          ) : (
            <div className="skeleton" style={{ height: 300 }} />
          )}
        </Card>
      </div>

      <Card>
        <CardHead title="Logo" sub={s.logoKey ? 'An uploaded logo' : 'The standard ISawBazaar logo'} />
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', paddingTop: 'var(--s3)' }}>
          <div style={{ background: '#1e293b', padding: 12, borderRadius: 10 }}>
            <img src={s.logoUrl} alt="Watermark logo" style={{ height: 56, display: 'block' }} />
          </div>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => onFile(e.target.files?.[0])} />
          <Button loading={uploadLogo.isPending} onClick={() => fileRef.current?.click()}>Upload a new logo</Button>
          {s.logoKey && (
            <Button loading={resetLogo.isPending} onClick={() => resetLogo.mutate()}>Use the standard logo</Button>
          )}
          <span className="cell-muted">PNG with a transparent background works best. Up to 1 MB.</span>
        </div>
      </Card>
    </div>
  );
}
